import { useEffect, useRef, useState } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight, faFolder, faFolderOpen, faHouse, faFileLines } from '@fortawesome/free-solid-svg-icons'
import type { FolderNode } from '../utils/folderTree'
import type { Template } from '../types'
import { colors, fontSize, radius, spacing, transition } from '../styles/theme'

// All of these are plain numbers, not theme tokens: `spacing.sm` is the string '8px', so
// `spacing.sm + depth * INDENT` silently yields '8px20' and the browser drops the declaration.
/** Left padding of a depth-0 row. */
const BASE_PAD = 8
/**
 * 20px per level — enough that a child's owner is unambiguous at a glance. A 25-level chain
 * runs past the sidebar, which is why the container scrolls horizontally.
 */
const INDENT_PX = 20
/** Guide line sits mid-chevron for the level it belongs to. */
const GUIDE_OFFSET = 8
/** Hovering a collapsed folder mid-drag opens it, so you can drill into a branch while dragging. */
const HOVER_EXPAND_MS = 700

export const ROOT_DROP_ID = 'folder-root'

export interface DragPayload {
  kind: 'template' | 'folder'
  id: string
  name: string
  /** Folders only: used to reject drops into the dragged folder's own subtree. */
  path?: string
}

/**
 * Vertical rules connecting each ancestor level to its children, so a row's owner is obvious
 * without counting pixels.
 */
function Guides({ depth }: { depth: number }) {
  if (depth <= 0) return null
  return (
    <>
      {Array.from({ length: depth }, (_, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            position: 'absolute',
            left: BASE_PAD + i * INDENT_PX + GUIDE_OFFSET,
            top: 0, bottom: 0,
            width: 1,
            background: colors.border,
          }}
        />
      ))}
    </>
  )
}

/** A template shown as a leaf of the tree. */
function TemplateLeaf({ template, depth, onOpen }: {
  template: Template
  depth: number
  onOpen: (templateId: string) => void
}) {
  const [hovered, setHovered] = useState(false)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `tree-template-${template.templateId}`,
    data: { kind: 'template', id: template.templateId, name: template.name } satisfies DragPayload,
  })

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: spacing.xs,
        // A file is a child of its folder, so it indents one level past it. No chevron offset:
        // the file icon lines up with the chevron of a subfolder at the same level.
        paddingLeft: BASE_PAD + depth * INDENT_PX,
        paddingRight: spacing.sm,
        height: 26,
        borderRadius: radius.sm,
        cursor: 'pointer',
        opacity: isDragging ? 0.4 : 1,
        background: hovered ? colors.surfaceMist : 'transparent',
        transition: `background ${transition.fast}`,
      }}
    >
      <Guides depth={depth} />
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={() => onOpen(template.templateId)}
        title={template.name}
        style={{
          display: 'flex', alignItems: 'center', gap: spacing.xs,
          flex: 1, minWidth: 0,
          fontSize: fontSize.sm,
          color: colors.textSecondary,
        }}
      >
        <FontAwesomeIcon icon={faFileLines} style={{ color: colors.textMuted, fontSize: 10, flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {template.name}
        </span>
      </div>
    </div>
  )
}

interface TreeRowProps {
  node: FolderNode
  selectedFolderId: string | null
  expanded: Set<string>
  templatesByFolder: Map<string | null, Template[]>
  onToggle: (folderId: string) => void
  onSelect: (folderId: string) => void
  onOpenTemplate: (templateId: string) => void
  /** Path of the folder currently being dragged, if any. Its subtree cannot accept the drop. */
  draggingFolderPath?: string
}

function TreeRow({
  node, selectedFolderId, expanded, templatesByFolder,
  onToggle, onSelect, onOpenTemplate, draggingFolderPath,
}: TreeRowProps) {
  const { folder, children, depth } = node
  const treeDepth = depth - 1                       // depth is 1-based; indent from 0
  const isOpen = expanded.has(folder.folderId)
  const isSelected = selectedFolderId === folder.folderId
  const templates = templatesByFolder.get(folder.folderId) ?? []
  // Direct files only — a parent's count does not roll up its children's.
  const fileCount = templates.length
  const hasContents = children.length > 0 || fileCount > 0

  // A folder may not be dropped into itself or its own descendants.
  const forbidden = !!draggingFolderPath && folder.path.startsWith(draggingFolderPath)

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder-${folder.folderId}`,
    data: { folderId: folder.folderId, path: folder.path },
    disabled: forbidden,
  })

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `drag-folder-${folder.folderId}`,
    data: { kind: 'folder', id: folder.folderId, name: folder.name, path: folder.path } satisfies DragPayload,
  })

  // Auto-expand on sustained hover during a drag.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (isOver && hasContents && !isOpen) {
      timer.current = setTimeout(() => onToggle(folder.folderId), HOVER_EXPAND_MS)
    }
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [isOver, hasContents, isOpen, folder.folderId, onToggle])

  const active = isOver && !forbidden

  return (
    <>
      <div
        ref={setDropRef}
        data-testid="folder-tree-node"
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', gap: 4,
          paddingLeft: BASE_PAD + treeDepth * INDENT_PX,
          paddingRight: spacing.sm,
          height: 28,
          borderRadius: radius.sm,
          cursor: 'pointer',
          opacity: isDragging ? 0.4 : 1,
          background: active ? colors.primaryLight : isSelected ? colors.surfaceTinted : 'transparent',
          boxShadow: active ? `inset 0 0 0 1px ${colors.primary}` : 'none',
          transition: `background ${transition.fast}`,
        }}
      >
        <Guides depth={treeDepth} />

        <button
          onClick={e => { e.stopPropagation(); onToggle(folder.folderId) }}
          aria-label={isOpen ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
          style={{
            width: 16, height: 16, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', padding: 0,
            cursor: hasContents ? 'pointer' : 'default',
            color: colors.textMuted, fontSize: 9,
            visibility: hasContents ? 'visible' : 'hidden',
          }}
        >
          <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
        </button>

        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          onClick={() => onSelect(folder.folderId)}
          title={folder.name}
          style={{
            display: 'flex', alignItems: 'center', gap: spacing.xs,
            flex: 1, minWidth: 0,
            fontSize: fontSize.sm,
            fontWeight: isSelected ? 500 : 400,
            color: isSelected ? colors.text : colors.textSecondary,
          }}
        >
          <FontAwesomeIcon
            icon={isOpen && hasContents ? faFolderOpen : faFolder}
            style={{ color: colors.textMuted, fontSize: 11, flexShrink: 0 }}
          />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {folder.name}
          </span>
          {fileCount > 0 && (
            <span style={{ color: colors.textMuted, fontSize: fontSize.xs, flexShrink: 0 }}>
              ({fileCount})
            </span>
          )}
        </div>
      </div>

      {isOpen && (
        <>
          {children.map(child => (
            <TreeRow
              key={child.folder.folderId}
              node={child}
              selectedFolderId={selectedFolderId}
              expanded={expanded}
              templatesByFolder={templatesByFolder}
              onToggle={onToggle}
              onSelect={onSelect}
              onOpenTemplate={onOpenTemplate}
              draggingFolderPath={draggingFolderPath}
            />
          ))}
          {templates.map(t => (
            <TemplateLeaf key={t.templateId} template={t} depth={depth} onOpen={onOpenTemplate} />
          ))}
        </>
      )}
    </>
  )
}

interface FolderTreeProps {
  tree: FolderNode[]
  selectedFolderId: string | null
  expanded: Set<string>
  templatesByFolder: Map<string | null, Template[]>
  onToggle: (folderId: string) => void
  onSelect: (folderId: string | null) => void
  onOpenTemplate: (templateId: string) => void
  draggingFolderPath?: string
}

export default function FolderTree({
  tree, selectedFolderId, expanded, templatesByFolder,
  onToggle, onSelect, onOpenTemplate, draggingFolderPath,
}: FolderTreeProps) {
  const { setNodeRef: setRootRef, isOver: isOverRoot } = useDroppable({
    id: ROOT_DROP_ID,
    data: { folderId: null },
  })

  const rootTemplates = templatesByFolder.get(null) ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 'max-content' }}>
      <div
        ref={setRootRef}
        onClick={() => onSelect(null)}
        data-testid="folder-tree-root"
        style={{
          display: 'flex', alignItems: 'center', gap: spacing.xs,
          padding: `0 ${spacing.sm}`,
          height: 28,
          borderRadius: radius.sm,
          cursor: 'pointer',
          fontSize: fontSize.sm,
          fontWeight: selectedFolderId === null ? 500 : 400,
          color: selectedFolderId === null ? colors.text : colors.textSecondary,
          background: isOverRoot ? colors.primaryLight : selectedFolderId === null ? colors.surfaceTinted : 'transparent',
          boxShadow: isOverRoot ? `inset 0 0 0 1px ${colors.primary}` : 'none',
          transition: `background ${transition.fast}`,
        }}
      >
        <FontAwesomeIcon icon={faHouse} style={{ color: colors.textMuted, fontSize: 11 }} />
        <span>All Templates</span>
        {rootTemplates.length > 0 && (
          <span style={{ color: colors.textMuted, fontSize: fontSize.xs }}>
            ({rootTemplates.length})
          </span>
        )}
      </div>

      {tree.map(node => (
        <TreeRow
          key={node.folder.folderId}
          node={node}
          selectedFolderId={selectedFolderId}
          expanded={expanded}
          templatesByFolder={templatesByFolder}
          onToggle={onToggle}
          onSelect={onSelect}
          onOpenTemplate={onOpenTemplate}
          draggingFolderPath={draggingFolderPath}
        />
      ))}

      {/* Root-level templates sit below the folders, matching how each folder lists its own. */}
      {rootTemplates.map(t => (
        <TemplateLeaf key={t.templateId} template={t} depth={0} onOpen={onOpenTemplate} />
      ))}
    </div>
  )
}

/** Expansion state persisted per session so navigating away doesn't collapse the tree. */
export function useExpandedFolders(storageKey = 'docm:templatesExpandedFolders') {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey)
      return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>()
    } catch {
      return new Set<string>()
    }
  })

  useEffect(() => {
    try { sessionStorage.setItem(storageKey, JSON.stringify([...expanded])) } catch { /* quota / private mode */ }
  }, [expanded, storageKey])

  const toggle = (folderId: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(folderId)) next.delete(folderId)
    else next.add(folderId)
    return next
  })

  const expandAll = (folderIds: string[]) => setExpanded(prev => new Set([...prev, ...folderIds]))

  return { expanded, toggle, expandAll }
}
